import { type CSSProperties, memo, useMemo } from 'react';

import { type Clip } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';

import { ClipThumbnail } from '~/components/Shared/ClipList/ClipItem/ClipThumbail';
import { MobileTableRowContainer } from '~/components/TableView/MobileTableRow/MobileTableRowContainer';
import { DEFAULT_THUMBNAIL_DIMENSIONS } from '~/components/TableView/NameCell/dimensions';
import { NameCellContent } from '~/components/TableView/NameCell/NameCellContent';
import { DesktopTableRowContainer } from '~/components/TableView/TableRow/DesktopTableRowContainer';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';

export type PlaceholderAssetTableRowProps = {
  asset: Clip;
  style: CSSProperties;
};

export const PlaceholderAssetTableRow = memo(({ asset, style }: PlaceholderAssetTableRowProps) => {
  const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
  const { isAboveMediumScreen } = useBreakpointsContext();

  const placeholderImage = useMemo(
    () => (
      <div className="relative size-full">
        <ClipThumbnail
          ext={asset.ext}
          status={asset.status}
          type={asset.type}
          borderRadius={4}
          src={asset.assets.image}
          hideText
          style={{ padding: 0 }}
          {...DEFAULT_THUMBNAIL_DIMENSIONS}
        />
        <div className="shimmer-background absolute inset-0 rounded-2 opacity-40" />
      </div>
    ),
    [asset.ext, asset.status, asset.type, asset.assets.image],
  );

  const placeholderTitle = useMemo(
    () => (
      <div className="relative ml-3 flex w-full flex-col items-start text-left">
        <span className="truncate text-14 text-grey-11">{asset.importedName ?? 'Untitled'}</span>
        <span className="text-12 text-grey-9">Importing...</span>
      </div>
    ),
    [asset.importedName],
  );

  return isAboveMediumScreen ? (
    <DesktopTableRowContainer
      style={{
        paddingLeft: responsiveHorizontalPadding,
        paddingRight: responsiveHorizontalPadding,
        ...style,
      }}
    >
      <div className="tableRow pointer-events-none flex items-center opacity-60">
        <NameCellContent disabled title={placeholderTitle} image={placeholderImage} />
      </div>
    </DesktopTableRowContainer>
  ) : (
    <MobileTableRowContainer
      style={{
        paddingLeft: responsiveHorizontalPadding,
        paddingRight: responsiveHorizontalPadding,
        ...style,
      }}
    >
      <NameCellContent disabled title={placeholderTitle} image={placeholderImage} />
    </MobileTableRowContainer>
  );
});

PlaceholderAssetTableRow.displayName = 'PlaceholderAssetTableRow';
